/**
 * # SplitCard
 *
 * A component that represents a split card layout, with sections and a wrapper.
 *
 * ## Props
 *
 * - `className?: string`: Additional CSS class name for the component.
 * - `roundness?: number`: The roundness of the card corners.
 * - `paddingLevel?: number`: The padding level of the card sections.
 * - `children?: React.ReactNode`: The content of the card.
 *
 * ### `SplitCard.Section` Props (inherits from `Card<T>`)
 *
 * - `as` (optional): The HTML element or React component to be used as the wrapper element. Defaults to `'div'`.
 * - `backgroundColor` (optional): The background color of the card. Defaults to `'white'`.
 * - `border` (optional): The type of border for the card. Can be `'none'`, `'regular'`, or `'darker'`. Defaults to `'regular'`.
 * - `className` (optional): Additional CSS class names for the card.
 * - `roundness` (optional): The roundness of the card corners. Can be `'small'`, `'medium'`, `'large'`, `'pill'`, or `'none'`. Defaults to `'small'`.
 * - `shadow` (optional): The shadow intensity of the card. Can be `'closest'`, `'close'`, `'default'`, `'far'`, or `'farthest'`.
 * - `paddingLevel` (optional): The padding level of the card. Can be a number or `null`. Defaults to `0`.
 * - `children` (optional): The content of the card.
 * - `...rest: T`: Any valid prop that can be passed to the component passed with the `as` prop.
 *
 * ## Usage
 *
 * ```tsx
 * import SplitCard from './SplitCard.react';
 *
 * const MyComponent = () => {
 *   return (
 *     <SplitCard>
 *       <SplitCard.Section>
 *         Section 1
 *       </SplitCard.Section>
 *       <SplitCard.Section>
 *         Section 2
 *       </SplitCard.Section>
 *     </SplitCard>
 *   );
 * };
 * ```
 */

import React, {forwardRef} from 'react';
import classnames from 'classnames';

import type {PaddingLevel} from '../../utils/paddingLevelProp';
import {
  getCardClassName,
  type CardBackgroundColor,
  type CardBorder,
  type CardRoundness,
  type CardShadow
} from '../../utils/getCardClassName';
import './split-card.scss';

interface SplitCardProps<C extends React.ElementType = 'div'>
  extends PropsWithChildrenOptional, PropsWithClassNameOptional {
  as?: C;
  backgroundColor?: CardBackgroundColor;
  border?: CardBorder;
  roundness?: CardRoundness;
  shadow?: CardShadow;
  paddingLevel?: PaddingLevel | null;
}

const Section = forwardRef(
  <C extends React.ElementType>({className, paddingLevel = 2, ...rest}: SplitCardProps<C>, ref) => {
    // shadow pulled out and never forwarded: a Section never renders a shadow
    const {
      as: Element = 'div',
      backgroundColor,
      border = 'regular',
      roundness,
      shadow,
      ...domRest
    } = rest;
    // A Section shows only a bottom divider (split-card.scss keeps the other sides at 0 and drops
    // it on the last section). An all-sides `border` utility would out-specify that override, so
    // emit just `border-b` here and let getCardClassName handle bg/roundness/padding.
    const divider =
      border === 'none'
        ? ''
        : classnames(
            'border-b border-solid',
            border === 'darker' ? 'border-border-bold' : 'border-border-primary'
          );
    return (
      <Element
        {...domRest}
        className={classnames(
          'split-card__section',
          getCardClassName({
            backgroundColor,
            border: 'none',
            roundness,
            shadow: undefined,
            paddingLevel
          }),
          divider,
          className
        )}
        ref={ref}
      />
    );
  }
);

type ChildProps = {
  roundness: 'small' | 'medium' | 'large' | 'pill' | 'none';
};

type SplitCardComponent = React.ForwardRefExoticComponent<SplitCardProps<React.ElementType>> &
  React.RefAttributes<unknown> & {Section: typeof Section};

const SplitCard: SplitCardComponent = Object.assign(
  forwardRef(
    <C extends React.ElementType>(
      {children, className, roundness, ...rest}: SplitCardProps<C>,
      ref
    ) => {
      const {as: Element = 'div', backgroundColor, border, shadow, paddingLevel, ...domRest} = rest;
      return (
        <Element
          {...domRest}
          className={classnames(
            'split-card__wrapper',
            getCardClassName({backgroundColor, border, roundness, shadow, paddingLevel}),
            className
          )}
          ref={ref}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                roundness
              } as ChildProps);
            }
            return null;
          })}
        </Element>
      );
    }
  ),
  {Section}
);

export default SplitCard;
